/*
 * skio-bundle-picker.js — custom Skio plan picker for Untammed (Mineral Mist).
 *
 * Replaces the Kaching "BUNDLE & SAVE" block. Renders purchase-type tabs, plan cards, a
 * mix-and-match flavour selector and a quantity stepper, then keeps hidden inputs inside the
 * product form in sync so the theme's own add-to-cart path does the actual work.
 *
 * Pricing rule: every figure displayed comes from Shopify's selling_plan_allocations, emitted
 * by the Liquid snippet. Nothing here applies a discount of its own, so the displayed price
 * cannot drift from what the cart charges.
 *
 * Gifts are added as $0 lines tagged with a `_skio_gift` rule; assets/skio-gift-guard.js
 * (loaded from layout/theme.liquid) enforces that rule for the life of the cart. Several
 * gifts per card are supported; the block's Free gift products picker is the normal source.
 */
(function () {
  'use strict';

  var MONEY = '${{amount}}';

  function money(cents, format, keepDecimals) {
    var value = (cents / 100).toFixed(2);
    if (!keepDecimals) value = value.replace(/\.00$/, '');
    var fmt = format || MONEY;
    // Only the two common Shopify placeholders are needed here.
    if (/amount_no_decimals/.test(fmt)) value = String(Math.round(cents / 100));
    return fmt.replace(/\{\{\s*amount(_no_decimals)?\s*\}\}/, value);
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function Picker(root) {
    this.root = root;
    this.mount = root.querySelector('[data-skio-bundle-mount]');
    this.inputs = root.querySelector('[data-skio-bundle-inputs]');
    this.config = JSON.parse(root.querySelector('[data-skio-bundle-config]').textContent);
    this.form = document.getElementById(this.config.formId);

    this.available = this.config.variants.filter(function (v) { return v.available; });
    this.cards = this.config.cards || [];

    /*
     * Gifts come from the Free gift products picker (config.gifts, built in Liquid) plus the
     * advanced JSON textarea (config.giftsExtraJson). A JSON gift whose variant the picker
     * already covers is dropped, so a merchant who fills both never doubles a gift.
     */
    this.gifts = (this.config.gifts || []).slice();
    var picked = (this.config.giftsPickedVariantIds || []).map(String);
    var self = this;
    try {
      (JSON.parse(this.config.giftsExtraJson || '[]') || []).forEach(function (g) {
        if (g && g.variantId && picked.indexOf(String(g.variantId)) === -1) self.gifts.push(g);
      });
    } catch (err) {
      console.warn('[skio-bundle-picker] Free gifts, advanced (JSON) is not valid JSON and was ignored:', err.message);
    }

    this.state = {
      mode: this.config.subscribeByDefault ? 'subscribe' : 'onetime',
      cardId: (this.cards[0] || {}).id,
      quantity: (this.cards[0] || {}).quantity || 1,
      flavours: []
    };
    this.syncFlavourCount();
    this.render();
    this.bindForm();
  }

  /*
   * A mix-and-match card has a floor (3 bags). The shopper may remove below it, so she can swap
   * a flavour out before picking its replacement, but the card stays incomplete until she is
   * back at the floor: the total line turns into "pick 1 more" and add-to-cart waits.
   */
  Picker.prototype.floorOf = function (card) {
    card = card || this.card();
    return (card && (card.minQuantity || card.quantity)) || 1;
  };
  Picker.prototype.missing = function () {
    var card = this.card();
    if (!card || !card.mixMatch) return 0;
    return Math.max(0, this.floorOf(card) - this.state.flavours.length);
  };
  /* "bag" from "/bag", "jar" from "/jar"; falls back to "bag". */
  Picker.prototype.unitWord = function (card) {
    card = card || this.card();
    var label = String((card && card.unitLabel) || '').replace(/^\s*\/\s*/, '').trim();
    return label || 'bag';
  };
  Picker.prototype.pickMoreText = function () {
    var n = this.missing();
    return 'Pick ' + n + ' more flavor' + (n === 1 ? '' : 's');
  };

  Picker.prototype.card = function (id) {
    var found = null;
    this.cards.forEach(function (c) { if (c.id === (id || this.state.cardId)) found = c; }, this);
    return found || this.cards[0];
  };

  /*
   * On a mix-and-match card the chips are the source of truth and quantity is derived from
   * them, so this only seeds the opening selection up to that card's floor. On every other
   * card quantity is the card's own and there is exactly one flavour.
   */
  Picker.prototype.syncFlavourCount = function () {
    var card = this.card();
    var pool = this.available;
    if (card && card.mixMatch) {
      var floor = card.minQuantity || card.quantity || 1;
      while (this.state.flavours.length < floor) {
        var i = this.state.flavours.length;
        this.state.flavours.push((pool[i % pool.length] || pool[0] || {}).id);
      }
      this.state.quantity = this.state.flavours.length;
      return;
    }
    /*
     * A fixed-flavour card owns its quantity through the stepper, so only lift it to the floor;
     * resetting it to card.quantity here made every + click a no-op (the stepper re-renders
     * through this method).
     */
    var minQty = (card && (card.minQuantity || card.quantity)) || 1;
    if (!this.state.quantity || this.state.quantity < minQty) this.state.quantity = minQty;
    this.state.flavours.length = 0;
    this.state.flavours.push((pool[0] || {}).id);
  };

  /* Unit price for a variant under the current mode. Allocation data, not arithmetic. */
  Picker.prototype.unit = function (variantId, card) {
    var variant = null;
    this.config.variants.forEach(function (v) { if (v.id === variantId) variant = v; });
    if (!variant) variant = this.config.variants[0];
    if (card === undefined) card = this.card();
    if (this.state.mode === 'subscribe' && card && card.planId) {
      var a = variant.allocations[String(card.planId)];
      if (a) return { price: a.price, compareAt: a.compareAt };
    }
    /*
     * An unselected card is a preview of what picking it would cost, so it has to be priced at
     * its OWN quantity. Using state.quantity (the selected card's) made the three-bag card read
     * $40 whenever the one-bag card was selected, because 1 never clears its 3-bag tier.
     */
    var qty = (card && card.id === this.state.cardId)
      ? this.state.quantity
      : (card && (card.quantity || card.minQuantity)) || 1;
    /*
     * A mix-and-match card that is short of its floor (mid-swap) still prices at the floor: she
     * cannot buy until she is back at 3, so the per-bag figure must not flicker to full price.
     */
    if (card && card.mixMatch && card.id === this.state.cardId) qty = Math.max(qty, this.floorOf(card));
    if (card && card.oneTimePercent
        && qty >= (card.oneTimeMinQuantity || 1)) {
      return {
        price: Math.round(variant.price * (1 - card.oneTimePercent / 100)),
        compareAt: variant.compareAt
      };
    }
    return { price: variant.price, compareAt: variant.compareAt };
  };

  /*
   * Gifts are $0 product lines, so the picker can add them directly. No app, no discount.
   * A card can carry several, and each can be limited to subscribers.
   */
  Picker.prototype.activeGifts = function (card) {
    var self = this;
    if (card === undefined) card = this.card();
    if (!card) return [];
    return this.gifts.filter(function (g) {
      /* No cardId = the first card, which is the bundle on every PDP. "*" = every card. */
      var target = g.cardId || (self.cards[0] || {}).id;
      if (target !== '*' && target !== card.id) return false;
      /*
       * mode comes from the two block pickers (Try Once list / Subscribe & Save list). The
       * older subscriptionOnly flag from the advanced JSON still works; a JSON gift with
       * neither is given on both tabs.
       */
      if (g.mode === 'subscribe' && self.state.mode !== 'subscribe') return false;
      if (g.mode === 'onetime' && self.state.mode !== 'onetime') return false;
      if (g.subscriptionOnly === true && self.state.mode !== 'subscribe') return false;
      return true;
    });
  };

  Picker.prototype.totals = function () {
    var self = this;
    var card = this.card();
    var ids = card && card.mixMatch
      ? this.state.flavours
      : new Array(this.state.quantity).fill(this.state.flavours[0]);
    var price = 0, compareAt = 0;
    ids.forEach(function (id) {
      var u = self.unit(id);
      price += u.price;
      compareAt += u.compareAt;
    });
    var perBag = ids.length ? Math.round(price / ids.length) : 0;
    return { price: price, compareAt: compareAt, perBag: perBag, count: ids.length, ids: ids };
  };

  Picker.prototype.savedPercent = function () {
    var t = this.totals();
    if (!t.compareAt || t.compareAt <= t.price) return 0;
    return Math.round(((t.compareAt - t.price) / t.compareAt) * 100);
  };

  Picker.prototype.fill = function (template) {
    var t = this.totals();
    var f = this.config.moneyFormat;
    return String(template || '')
      .replace(/\{qty\}/g, t.count)
      .replace(/\{perBagFull\}/g, money(t.perBag, f, true))
      .replace(/\{totalFull\}/g, money(t.price, f, true))
      .replace(/\{perBag\}/g, money(t.perBag, f))
      .replace(/\{total\}/g, money(t.price, f))
      .replace(/\{saved\}/g, this.savedPercent() + '%');
  };

  /*
   * Like fill(), but for a specific card whether or not it is selected. The selected card uses
   * the live totals (its chips and stepper); an unselected card is priced at its own quantity,
   * the same rule unit() applies to the price column. Used for the billing line under the
   * price ("$96 billed every 3 months"), which shows on every card like AG1's.
   */
  Picker.prototype.fillFor = function (template, card) {
    if (card.id === this.state.cardId) return this.fill(template);
    var qty = card.quantity || card.minQuantity || 1;
    var u = this.unit(this.state.flavours[0], card);
    var price = u.price * qty;
    var f = this.config.moneyFormat;
    return String(template || '')
      .replace(/\{qty\}/g, qty)
      .replace(/\{perBagFull\}/g, money(u.price, f, true))
      .replace(/\{totalFull\}/g, money(price, f, true))
      .replace(/\{perBag\}/g, money(u.price, f))
      .replace(/\{total\}/g, money(price, f))
      .replace(/\{saved\}/g, (u.compareAt > u.price ? Math.round(((u.compareAt - u.price) / u.compareAt) * 100) : 0) + '%');
  };

  Picker.prototype.render = function () {
    var self = this;
    this.mount.textContent = '';

    if (this.config.blockTitle) {
      var head = el('div', 'skio-bundle__title');
      head.appendChild(el('span', null, this.config.blockTitle));
      this.mount.appendChild(head);
    }

    /* Purchase-type tabs */
    var tabs = el('div', 'skio-bundle__tabs');
    [['onetime', this.config.onetimeTitle], ['subscribe', this.config.subscribeTitle]].forEach(function (pair) {
      var btn = el('button', 'skio-bundle__tab' + (self.state.mode === pair[0] ? ' is-selected' : ''), pair[1]);
      btn.type = 'button';
      btn.setAttribute('aria-pressed', self.state.mode === pair[0] ? 'true' : 'false');
      btn.addEventListener('click', function () {
        self.state.mode = pair[0];
        self.render();
        self.syncInputs();
      });
      tabs.appendChild(btn);
    });
    this.mount.appendChild(tabs);

    this.cards.forEach(function (card) { self.mount.appendChild(self.renderCard(card)); });
    this.syncInputs();
    this.syncSubmit();
  };

  /*
   * The theme's add-to-cart button waits while the ritual is short of its floor. The label is
   * swapped in place ("Pick 1 more flavor") and restored the moment she is back at 3, so the
   * theme's own loader and "added" states keep working.
   */
  Picker.prototype.syncSubmit = function () {
    var button = this.form && (this.form.querySelector('[name="add"]')
      || document.querySelector('[name="add"][form="' + this.config.formId + '"]'));
    if (!button) return;
    var label = button.querySelector('[data-add-to-cart-text]') || button.querySelector('.btn__text') || button;
    var short = this.missing() > 0;
    if (short) {
      if (!button.dataset.skioLabel) button.dataset.skioLabel = label.textContent;
      label.textContent = this.pickMoreText();
      button.disabled = true;
      button.classList.add('skio-bundle-wait');
    } else if (button.dataset.skioLabel) {
      label.textContent = button.dataset.skioLabel;
      delete button.dataset.skioLabel;
      button.disabled = false;
      button.classList.remove('skio-bundle-wait');
    }
  };

  Picker.prototype.renderCard = function (card) {
    var self = this;
    var selected = card.id === this.state.cardId;
    var wrap = el('div', 'skio-bundle__card' + (selected ? ' is-selected' : '')
      + (card.badge ? ' has-badge' : ''));

    /*
     * Ribbons straddle the top border. `label` (FREE SHIPPING) sits top-left, `badge` top-right;
     * the current templates only use the label, so the card reads like AG1's.
     */
    if (card.label) { wrap.classList.add('has-badge'); wrap.appendChild(el('span', 'skio-bundle__badge skio-bundle__badge--left', card.label)); }
    if (card.badge) wrap.appendChild(el('span', 'skio-bundle__badge', card.badge));

    var header = el('button', 'skio-bundle__card-head');
    header.type = 'button';
    header.addEventListener('click', function () {
      /* Re-clicking the selected plan must not wipe her chips. Each plan has its own floor. */
      if (self.state.cardId === card.id) return;
      self.state.cardId = card.id;
      self.state.flavours.length = 0;
      self.state.quantity = card.quantity || 1;
      self.syncFlavourCount();
      self.render();
    });

    var radio = el('span', 'skio-bundle__radio' + (selected ? ' is-on' : ''));
    header.appendChild(radio);

    /*
     * Header layout, AG1-style, three stacked rows under the radio:
     *   title ................................ SAVE 20%
     *   $32 /bag  $40 ........................ $1.14 per serving
     *   $96 billed every 3 months
     * Per-serving shares the price row so the billing line gets the full card width and
     * never has to break around it.
     */
    var titles = el('span', 'skio-bundle__titles');
    var unitInfo = this.unit(this.state.flavours[0], card);
    var unitPrice = selected ? this.totals().perBag : unitInfo.price;
    var unitCompare = unitInfo.compareAt;

    /* Row 1: title + save sticker. The sticker is skipped when there is nothing saved. */
    var titleRow = el('span', 'skio-bundle__title-row');
    titleRow.appendChild(el('strong', 'skio-bundle__card-title', card.title));
    var savedPct = selected
      ? this.savedPercent()
      : (unitCompare > unitPrice ? Math.round(((unitCompare - unitPrice) / unitCompare) * 100) : 0);
    if (savedPct > 0) titleRow.appendChild(el('span', 'skio-bundle__save', 'SAVE ' + savedPct + '%'));
    titles.appendChild(titleRow);

    /* Row 2: unit price, unit label, compare-at on the left; per-serving pushed to the right. */
    var priceRow = el('span', 'skio-bundle__price-row');
    priceRow.appendChild(el('span', 'skio-bundle__price', money(unitPrice, this.config.moneyFormat)));
    if (card.unitLabel) priceRow.appendChild(el('span', 'skio-bundle__unit', card.unitLabel));
    if (unitCompare > unitPrice) priceRow.appendChild(el('s', 'skio-bundle__was', money(unitCompare, this.config.moneyFormat)));
    if (card.perServing && this.config.servings) {
      /*
       * perServingAddPerUnit folds a per-bag cost the cart charges anyway (shipping on the
       * single bag) into the per-serving figure, so the cards compare like for like.
       */
      var per = unitPrice + (card.perServingAddPerUnit || 0);
      var perServing = Math.round(per / this.config.servings);
      /* "/serving" hugs the figure (AG1 style, "$2.30/serving"); "per serving" keeps its space. */
      var suffix = this.config.perServingSuffix || 'per serving';
      priceRow.appendChild(el('span', 'skio-bundle__per-serving',
        money(perServing, this.config.moneyFormat) + (suffix.charAt(0) === '/' ? '' : ' ') + suffix));
    }
    titles.appendChild(priceRow);

    /*
     * Row 3: the billing line, full width, one plain string. On the one-time tab, where there
     * is no billing cadence, it carries the bundle total instead so that never disappears
     * from the card. A single bag bought once has nothing to say here, so the row is skipped.
     */
    var billingTpl = this.state.mode === 'subscribe' ? card.billing : card.billingOneTime;
    var qtyForCard = selected ? this.totals().count : (card.quantity || card.minQuantity || 1);
    var billingText = billingTpl ? this.fillFor(billingTpl, card)
      : (qtyForCard > 1 ? this.fillFor('{total} total', card) : '');
    var short = selected && this.missing() > 0;
    if (short) billingText = this.pickMoreText() + ' to complete your ritual';
    if (billingText) {
      var metaRow = el('span', 'skio-bundle__meta-row');
      metaRow.appendChild(el('span', 'skio-bundle__billing' + (short ? ' is-short' : ''), billingText));
      titles.appendChild(metaRow);
    }

    header.appendChild(titles);
    wrap.appendChild(header);

    if (!selected) {
      var quiet = this.disclosure(card);
      if (quiet) wrap.appendChild(quiet);
      return wrap;
    }

    /* Full-width rule between the price block and the highlights, like AG1's card. */
    wrap.appendChild(el('div', 'skio-bundle__divider'));

    var body = el('div', 'skio-bundle__body');

    /* Optional per-tab copy: highlightsSubscribe / highlightsOneTime override the shared list. */
    var highlights = card.highlights;
    if (this.state.mode === 'subscribe' && card.highlightsSubscribe) highlights = card.highlightsSubscribe;
    if (this.state.mode === 'onetime' && card.highlightsOneTime) highlights = card.highlightsOneTime;

    if (highlights && highlights.length) {
      var list = el('ul', 'skio-bundle__highlights');
      highlights.forEach(function (item) {
        /* A "SAVE {saved}" line reads as "SAVE 0%" on the one-time tab, so drop it there. */
        if (/\{saved\}/.test(item) && self.savedPercent() === 0) return;
        var li = el('li', null, null);
        li.appendChild(el('span', 'skio-bundle__check', '✓'));
        li.appendChild(el('span', null, self.fill(item)));
        list.appendChild(li);
      });
      body.appendChild(list);
    }

    if (this.available.length > 1) {
      var label = el('div', 'skio-bundle__flavours-label',
        this.config.flavorsLabel || 'FLAVORS');
      if (card.mixMatch) {
        /* The grid carries quantity now, so the bag count has to stay legible somewhere. */
        var n = this.state.quantity, floor = this.floorOf(card), unitWord = this.unitWord(card);
        var countText = n < floor
          ? n + ' of ' + floor + ' · pick ' + (floor - n) + ' more'
          : n + ' ' + unitWord + (n === 1 ? '' : 's');
        label.appendChild(el('span', 'skio-bundle__flavours-count' + (n < floor ? ' is-short' : ''), countText));
      }
      body.appendChild(label);
      if (card.mixMatch) {
        /* The rule, said once, so the floor never has to be discovered through a dead button. */
        var hint = this.config.flavorsHint == null
          ? 'Tap a flavor to add a ' + this.unitWord(card) + '. Tap the minus to swap one out.'
          : this.config.flavorsHint;
        /* No hyphen in "Minimum 3 bags": PT Mono breaks "3-bag" at the dash on narrow cards. */
        if (hint) body.appendChild(el('p', 'skio-bundle__flavours-hint',
          'Minimum ' + this.floorOf(card) + ' ' + this.unitWord(card) + 's. ' + hint));
      }
      body.appendChild(this.renderFlavourGrid(card));
    }

    /* On a mix-and-match card the grid sets quantity, so a stepper would fight it. */
    if (card.stepper && !card.mixMatch) body.appendChild(this.renderStepper(card));

    var gifts = this.activeGifts(card);
    if (this.config.giftLayout === 'tiles') {
      if (gifts.length) body.appendChild(this.renderGiftTiles(gifts));
    } else {
      gifts.forEach(function (g) {
        var gift = el('div', 'skio-bundle__gift');
        var giftImage = g.image || self.config.giftImage;
        if (giftImage) {
          var gimg = document.createElement('img');
          gimg.className = 'skio-bundle__gift-thumb';
          gimg.src = giftImage;
          gimg.alt = '';
          gimg.loading = 'lazy';
          gift.appendChild(gimg);
        }
        gift.appendChild(el('span', 'skio-bundle__gift-text', g.text));
        if (g.price) gift.appendChild(el('s', 'skio-bundle__was', money(g.price, self.config.moneyFormat)));
        body.appendChild(gift);
      });
    }

    wrap.appendChild(body);
    var note = this.disclosure(card);
    if (note) wrap.appendChild(note);
    return wrap;
  };

  /*
   * Tiles: gifts side by side, each a square image with a FREE badge (struck price inside it)
   * and the name underneath. Saves the vertical space the rows take once there are three or
   * more gifts. The "+ FREE" wording prefix is dropped here because the badge already says it.
   */
  Picker.prototype.renderGiftTiles = function (gifts) {
    var self = this;
    var wrap = el('div', 'skio-bundle__gifts');
    if (this.config.giftHeading) {
      wrap.appendChild(el('div', 'skio-bundle__gifts-heading', this.config.giftHeading));
    }
    var grid = el('div', 'skio-bundle__gifts-tiles');
    gifts.forEach(function (g) {
      var tile = el('div', 'skio-bundle__tile');
      var media = el('div', 'skio-bundle__tile-media');
      var giftImage = g.image || self.config.giftImage;
      if (giftImage) {
        var img = document.createElement('img');
        img.className = 'skio-bundle__tile-img';
        img.src = giftImage;
        img.alt = '';
        img.loading = 'lazy';
        media.appendChild(img);
      }
      var badge = el('span', 'skio-bundle__tile-badge', 'FREE');
      if (g.price) badge.appendChild(el('s', null, money(g.price, self.config.moneyFormat)));
      media.appendChild(badge);
      tile.appendChild(media);
      var name = String(g.text || '').replace(/^\+?\s*free\s+/i, '');
      tile.appendChild(el('span', 'skio-bundle__tile-name', name));
      grid.appendChild(tile);
    });
    wrap.appendChild(grid);
    return wrap;
  };

  /* Renewal disclosure. Subscription-only by design: it is the compliance-sensitive copy. */
  Picker.prototype.disclosure = function (card) {
    if (this.state.mode !== 'subscribe' || !card.disclosure) return null;
    if (card.id !== this.state.cardId) return null;
    return el('div', 'skio-bundle__note', this.fill(card.disclosure));
  };

  /*
   * Every in-stock flavour is a chip. On a mix-and-match card a click adds a bag of that
   * flavour and the chip carries its own count plus a minus, so three bags of one flavour is
   * as reachable as three different ones (which is what the reviews actually ask for).
   * Elsewhere the chips are a plain single-select.
   * Out-of-stock flavours are left out, matching hideUnavailableOptions in the old block.
   */
  Picker.prototype.renderFlavourGrid = function (card) {
    var self = this;
    var multi = !!(card && card.mixMatch);
    var grid = el('div', 'skio-bundle__flavours');

    /*
     * Chips sit two to a row to save the vertical space the dropdowns used to eat. PT Mono is
     * wide enough that a long flavour name cannot share a row without truncating, so anything
     * past LONG_NAME takes a full row of its own and sorts to the bottom. This is display
     * order only. The seeding order in syncFlavourCount is deliberately left alone so the
     * three pre-selected flavours do not change.
     */
    var LONG_NAME = 20;
    var ordered = this.available.slice().sort(function (a, b) {
      return (String(a.title).length > LONG_NAME) - (String(b.title).length > LONG_NAME);
    });

    ordered.forEach(function (v) {
      var count = self.state.flavours.filter(function (id) { return id === v.id; }).length;
      var wide = String(v.title).length > LONG_NAME;
      var chip = el('div', 'skio-bundle__chip'
        + (wide ? ' skio-bundle__chip--wide' : '')
        + (count ? ' is-selected' : ''));

      var pick = el('button', 'skio-bundle__chip-pick');
      pick.type = 'button';
      pick.setAttribute('aria-pressed', count ? 'true' : 'false');
      pick.setAttribute('aria-label', (multi ? 'Add a bag of ' : 'Choose ') + v.title);
      if (v.image) {
        var thumbWrap = el('span', 'skio-bundle__chip-thumbwrap');
        var img = document.createElement('img');
        img.className = 'skio-bundle__chip-thumb';
        img.src = v.image;
        img.alt = '';
        img.loading = 'lazy';
        thumbWrap.appendChild(img);
        /*
         * The count rides on the thumb, not in the row. Measured at a 375px viewport, the old
         * inline count and minus took 42px and left the name only 57px, while "Grapefruit"
         * needs 63px, which is exactly why selected chips broke mid-word.
         */
        pick.appendChild(thumbWrap);
      }
      pick.appendChild(el('span', 'skio-bundle__chip-name', v.title));
      /* "×2" beside the name: a second tap on Lemon gave her a second Lemon, and it says so. */
      if (multi && count > 1) pick.appendChild(el('span', 'skio-bundle__chip-count', '\u00d7' + count));
      pick.addEventListener('click', function () {
        if (multi) {
          self.state.flavours.push(v.id);
          self.state.quantity = self.state.flavours.length;
        } else {
          self.state.flavours = [v.id];
          self.state.quantity = (card && card.quantity) || 1;
        }
        self.render();
      });
      chip.appendChild(pick);

      if (multi && count) {
        var minus = el('button', 'skio-bundle__chip-minus', '−');
        minus.type = 'button';
        minus.setAttribute('aria-label', 'Remove a bag of ' + v.title);
        /*
         * Removing is always allowed, down to the last bag. Below the floor the card asks for
         * the replacement (see missing()) instead of greying this button out, which is what the
         * session replays showed shoppers rage-clicking.
         */
        minus.disabled = self.state.flavours.length <= 1;
        minus.addEventListener('click', function (e) {
          e.stopPropagation();
          if (self.state.flavours.length <= 1) return;
          var at = self.state.flavours.indexOf(v.id);
          if (at > -1) self.state.flavours.splice(at, 1);
          self.state.quantity = self.state.flavours.length;
          self.render();
        });
        chip.appendChild(minus);
      }

      grid.appendChild(chip);
    });
    return grid;
  };

  Picker.prototype.renderStepper = function (card) {
    var self = this;
    var min = card.minQuantity || card.quantity || 1;
    var wrap = el('div', 'skio-bundle__stepper');

    function step(label, delta) {
      var b = el('button', 'skio-bundle__step', label);
      b.type = 'button';
      b.addEventListener('click', function () {
        var next = self.state.quantity + delta;
        if (next < min) return;
        self.state.quantity = next;
        self.syncFlavourCount();
        self.render();
      });
      return b;
    }

    wrap.appendChild(step('−', -1));
    wrap.appendChild(el('span', 'skio-bundle__qty', String(this.state.quantity)));
    wrap.appendChild(step('+', 1));
    return wrap;
  };

  /* Hidden inputs the theme's FormData will pick up. */
  Picker.prototype.syncInputs = function () {
    var self = this;
    var card = this.card();
    var t = this.totals();
    this.inputs.textContent = '';

    t.ids.forEach(function (variantId, i) {
      function add(name, value) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'items[' + i + '][' + name + ']';
        input.value = value;
        if (self.config.formId) input.setAttribute('form', self.config.formId);
        self.inputs.appendChild(input);
      }
      add('id', variantId);
      add('quantity', 1);
      if (self.state.mode === 'subscribe' && card && card.planId) add('selling_plan', card.planId);
    });

    var gi = t.ids.length;
    this.activeGifts(card).forEach(function (g) {
      if (!g.variantId) return;
      /*
       * The gift is a $0 line the cart knows nothing about, so it used to survive the customer
       * dropping below the bundle. `_skio_gift` (underscore = hidden from the customer) carries
       * the rule the gift depends on; skio-gift-guard.js reads it on every cart change and
       * removes the gift when the rule stops holding.
       */
      var rule = JSON.stringify({
        p: self.config.productId,
        min: (card && (card.minQuantity || card.quantity)) || 1,
        sub: g.mode === 'subscribe' || g.subscriptionOnly === true,
        q: g.quantity || 1
      });
      [['id', g.variantId], ['quantity', g.quantity || 1], ['properties][_skio_gift', rule]].forEach(function (pair) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'items[' + gi + '][' + pair[0] + ']';
        input.value = pair[1];
        if (self.config.formId) input.setAttribute('form', self.config.formId);
        self.inputs.appendChild(input);
      });
      gi += 1;
    });

    /* Keep the theme's own variant input pointing at something sane for its other features. */
    var themeId = document.querySelector('[name="id"][form="' + this.config.formId + '"]');
    if (themeId && t.ids[0]) themeId.value = t.ids[0];
  };

  /*
   * The theme builds FormData synchronously when the add-to-cart button is clicked. Shopify adds
   * an extra planless line if the theme's own `id` and `quantity` fields survive alongside
   * items[], so they are disabled for the duration of that read and restored immediately after.
   */
  Picker.prototype.bindForm = function () {
    var self = this;
    if (!this.form) return;

    function suppress() {
      var nodes = [
        document.querySelector('[name="id"][form="' + self.config.formId + '"]'),
        self.form.querySelector('[name="id"]'),
        self.form.querySelector('[name="quantity"]'),
        document.querySelector('[name="quantity"][form="' + self.config.formId + '"]')
      ].filter(Boolean);
      nodes.forEach(function (n) { n.disabled = true; });
      setTimeout(function () { nodes.forEach(function (n) { n.disabled = false; }); }, 0);
    }

    var button = this.form.querySelector('[name="add"]')
      || document.querySelector('[name="add"][form="' + this.config.formId + '"]');
    if (button) button.addEventListener('click', function () { self.syncInputs(); suppress(); }, true);
    this.form.addEventListener('submit', function (e) {
      if (self.missing() > 0) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var count = self.mount.querySelector('.skio-bundle__flavours-count');
        if (count && count.scrollIntoView) count.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }
      self.syncInputs();
      suppress();
    }, true);
  };

  function init() {
    document.querySelectorAll('[data-skio-bundle]').forEach(function (root) {
      if (root.dataset.skioReady) return;
      root.dataset.skioReady = '1';
      try {
        new Picker(root);
      } catch (err) {
        console.error('[skio-bundle-picker]', err);
        /* In the theme editor, say so on the page instead of leaving a blank buy box. */
        if (window.Shopify && Shopify.designMode) {
          var mount = root.querySelector('[data-skio-bundle-mount]');
          if (mount) {
            mount.textContent = 'Skio Bundle Picker could not load. Check that Plan cards (JSON) '
              + 'is valid: ' + err.message;
            mount.setAttribute('style', 'padding:1rem;border:2px dashed #c85c42;color:#c85c42;'
              + 'border-radius:12px;font:inherit');
          }
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
